import {postProcessingSettingsToImageProcessingSettings} from "@app/template-editor/helpers/template-jobs"
import type {CmLogger} from "@cm/log"
import {ImageProcessingNodes} from "@cm/image-processing-nodes"
import {MaterialThumbnailPostRenderStepInput, materialThumbnailPostRenderStepTask} from "@cm/job-nodes/asset-rendering"
import {imageProcessingTask} from "@cm/job-nodes/image-processing"
import {CreateJobGraphData, JobNodes} from "@cm/job-nodes/job-nodes"
import {cmRenderTaskForPassNames} from "@cm/job-nodes/rendering"
import {createPostProcessingGraph, Utility} from "@cm/job-nodes/utility"
import {DataResolver, MaterialGraph} from "@cm/material-nodes"
import {SceneNodes} from "@cm/template-nodes"
import {CM_TO_INCH, tileParamsForMaterial} from "@common/helpers/rendering/material/material-assets-rendering/common"
import {JobGraphMaterial} from "@common/models/material-assets-rendering/job-graph-material"
import {Settings} from "@common/models/settings/settings"
import {generateThumbnailsDataObjectTask} from "@cm/job-nodes"

export function jobGraphFn_thumbnail(
    renderGraphDataObjectId: number,
    materialDetails: JobGraphMaterial,
    size: number,
    sizeCm: number,
    filenames: MaterialThumbnailPostRenderStepInput["filenames"],
    postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined,
): CreateJobGraphData {
    const mainRenderTask = JobNodes.task(cmRenderTaskForPassNames("Combined"), {
        input: JobNodes.value({
            renderGraph: JobNodes.dataObjectReference(renderGraphDataObjectId),
            customerId: materialDetails.organization.legacyId,
            previewForProgressValue: [],
        }),
    })

    const combinedRenderTask = JobNodes.struct({
        renderPasses: JobNodes.get(mainRenderTask, "renderPasses"),
        preview: JobNodes.get(mainRenderTask, "preview"),
        metadata: JobNodes.get(mainRenderTask, "metadata"),
    })

    const createPostProcessingGraphTask = JobNodes.task(createPostProcessingGraph, {
        input: JobNodes.struct({
            data: combinedRenderTask,
            settings: JobNodes.value(postProcessingSettings ? postProcessingSettingsToImageProcessingSettings(postProcessingSettings) : undefined),
            cryptomatteManifest: JobNodes.struct({
                type: JobNodes.value("externalData" as const),
                resolveTo: JobNodes.value("cryptomatteManifest" as const),
                sourceData: JobNodes.get(combinedRenderTask, "metadata"),
            }),
        }),
    })

    const dpi = size / (sizeCm * CM_TO_INCH)
    const setDpi: JobNodes.TypedDataNode<ImageProcessingNodes.SetDpi> = JobNodes.struct({
        type: JobNodes.value("setDpi" as const),
        input: JobNodes.get(createPostProcessingGraphTask, "image"),
        dpi: JobNodes.value(dpi),
    })
    const encodedImage: JobNodes.TypedDataNode<ImageProcessingNodes.Encode> = JobNodes.struct({
        type: JobNodes.value("encode" as const),
        input: setDpi,
        mediaType: JobNodes.value("image/x-exr"),
    })
    const imgProcTask = JobNodes.task(imageProcessingTask, {input: JobNodes.struct({graph: encodedImage})})

    const uploadProcessingTaskNode = JobNodes.task(generateThumbnailsDataObjectTask, {
        input: Utility.DataObject.update(imgProcTask, {imageDataType: "COLOR"}),
    })

    const postRenderTask = JobNodes.task(materialThumbnailPostRenderStepTask, {
        input: JobNodes.struct({
            renderImage: JobNodes.get(uploadProcessingTaskNode, "dataObject"),
            sizeCm: JobNodes.value(sizeCm),
            materialId: JobNodes.value(materialDetails.legacyId),
            filenames: JobNodes.value(filenames),
        }),
    })

    return JobNodes.jobGraph(postRenderTask, {
        progress: {
            type: "progressGroup",
            items: [
                {node: mainRenderTask, factor: 18},
                {node: uploadProcessingTaskNode, factor: 2},
                {node: postRenderTask, factor: 1},
            ],
        },
        platformVersion: Settings.APP_VERSION,
    })
}

export async function thumbnailGenerationParamsForMaterial(materialGraph: MaterialGraph, resolve: DataResolver, logger: CmLogger) {
    const tileParms = await tileParamsForMaterial(materialGraph, resolve, logger)
    return {
        offsetX: tileParms.repeatWidthCm / 2,
        offsetY: tileParms.repeatHeightCm / 2,
    }
}
